/* eslint-disable max-len */
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';

export default class ArithmeticNode extends BaseNode {
    operator: string;

    constructor() {
        super();
        this.linkedFeature = 'general';
        this.name = 'Calculate';
        this.icon = 'TbMath';
        this.id = 'node_arithmetic';
        this.isAsync = true;

        const settings: Setting[] = [
            {
                id: 'VARIABLE',
                name: 'Variable',
                settingType: SettingType.OPTION,
                required: false,
                refId: 'variable',
                default: '',
                options: [],
            },
        ];

        this.settings = this.settings.concat(settings);

        this.inputs = [
            {
                name: 'Exec',
                portId: 'exec',
                params: [
                    {
                        name: 'operand1',
                        dataType: TibboDataType.CUSTOM,
                        default: '0',
                    },
                    {
                        name: 'operator',
                        dataType: TibboDataType.CUSTOM,
                        default: '+',
                    },
                    {
                        name: 'operand2',
                        dataType: TibboDataType.CUSTOM,
                        default: '0',
                    },
                ],
            },
        ];

        this.outputs = [
            {
                name: 'Answer',
                portId: 'answer',
                params: [
                    {
                        name: 'result',
                        dataType: TibboDataType.FLOAT,
                    },
                ],
            },
        ];
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const { project } = this;
        if (settingId === 'operator') {
            return [
                {
                    name: 'Add',
                    value: '+',
                },
                {
                    name: 'Subtract',
                    value: '-',
                },
                {
                    name: 'Multiply',
                    value: '*',
                },
                {
                    name: 'Divide',
                    value: '/',
                },
                {
                    name: 'Modulo (%)',
                    value: 'mod',
                },
            ];
        }
        return super.getSettingOptions(settingId);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const links = project.getNodeLinks(this, 'exec');
            if (links.length > 0) {
                const VARIABLE = this.getSettingValue('VARIABLE');
                let varName = '';
                if (VARIABLE !== '') {
                    const assignedVariable = project.getFeature(VARIABLE);
                    varName = `{{$${assignedVariable?.getSettingValue('ID')}}}`;
                } else {
                    varName = 'result';
                }
                const link = links[0];
                const op1 = project.getNodeParameterValue(this, 'operand1');
                const op2 = project.getNodeParameterValue(this, 'operand2');
                let OPERATOR = project.getNodeParameter(link, 'operator');
                if (OPERATOR === 'mod') {
                    OPERATOR = '%';
                }
                return `${varName} = ${op1} ${OPERATOR} ${op2}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const links = project.getNodeLinks(this, 'exec');
        if (links.length > 0) {
            const VARIABLE = this.getSettingValue('VARIABLE');
            let varName = '';
            if (VARIABLE !== '') {
                const assignedVariable = project.getFeature(VARIABLE);
                varName = `{{$${assignedVariable?.getSettingValue('ID')}}}`;
            } else {
                varName = 'result';
            }
            const link = links[0];
            const op1 = project.getNodeParameterValue(this, 'operand1');
            const op2 = project.getNodeParameterValue(this, 'operand2');
            const OPERATOR = project.getNodeParameter(link, 'operator');
            return `${varName} = ${op1} ${OPERATOR} ${op2}`;
        }
        return this.name;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;
        const OPERATOR = this.getSettingValue('OPERATOR');

        const incomingLinks = project.getNodeLinks(this, 'exec');
        const outgoing = project.getNodeLinks(this, 'answer');
        const nodeID = this.getID();

        for (let i = 0; i < incomingLinks.length; i += 1) {
            // const link = outgoing[0];
            // const targetParams = project.getFeature(link.getSettingValue('TARGET')).getSettingValue('PARAMS');
            // let targetDataType = TibboDataType.STRING;

            // args[0].setSettingValue('ARGTYPE', 'expression');
            // link.getSettingValue('ARGUMENTS')[0].setSettingValue('ARGTYPE', 'expression');
            // const targetParamName = link.getSettingValue('ARGUMENTS')[0].getSettingValue('TARGETPARAM');

            // for (let i = 0; i < targetParams.length; i += 1) {
            //     if (targetParams[i].getSettingValue('NAME') === targetParamName) {
            //         targetDataType = targetParams[i].getSettingValue('DATATYPE');
            //         break;
            //     }
            // }

            // expression = incomingLinks.map((incomingLink) => {
            //     return incomingLink.getSettingValue('ARGUMENTS')[0].getSettingValue('ARGVALUE');
            // }).join(this.operator);
            const link = incomingLinks[i];

            const op1 = project.getNodeParameter(link, 'operand1');
            let operator = project.getNodeParameter(link, 'operator').replace(/"/g, '');
            if (operator === 'mod') {
                if (project.runtime !== 'tios') {
                    operator = '%';
                }
            }
            const op2 = project.getNodeParameter(link, 'operand2');

            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `calc_${nodeID}(${op1} ${operator} ${op2})${project.runtime === 'zephyr' ? ';' : ''}`,
            });
        }

        let assignmentCode = '';
        let resultCode = '';
        const VARIABLE = this.getSettingValue('VARIABLE');
        if (VARIABLE !== '') {
            const assignedVariable = project.getFeature(VARIABLE);
            const varName = assignedVariable?.getSettingValue('VARNAME');
            const varType = assignedVariable?.getSettingValue('DATATYPE');
            const exp = project.convertDataType(
                'result',
                TibboDataType.FLOAT,
                varType,
            );
            assignmentCode = `var_${varName}_set(${exp})${project.runtime === 'zephyr' ? ';' : ''}`;
        }

        if (outgoing.length > 0) {
            resultCode = `${project.getCodeLocationStart()}${outgoing[0].getSourceId()}${project.getCodeLocationEnd()}`;
        }
        if (project.runtime === 'tios') {
            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                id: `declare_calc_${nodeID}`,
                template: `
declare sub calc_${nodeID}(result as float)
    `,
            });
            blocks.push({
                location: 'DEVICE_TBS',
                blockType: BlockType.CODE,
                id: `calc_${nodeID}`,
                template: `
sub calc_${nodeID}(result as float)
    ${assignmentCode}
    ${resultCode}
end sub
`,
            });
        }

        if (project.runtime === 'micropython') {
            blocks.push({
                location: 'FUNCTION_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
def calc_${nodeID}(result):
    ${assignmentCode}
    ${resultCode}
    pass
`,
            });
        }

        if (project.runtime === 'zephyr') {
            blocks.push({
                location: 'FUNCTION_DECLARATIONS',
                blockType: BlockType.CODE,
                id: `declare_calc_${nodeID}`,
                template: `
void calc_${nodeID}(float result);`,
            });
            blocks.push({
                location: 'FUNCTION_DEFINITIONS',
                blockType: BlockType.CODE,
                id: `calc_${nodeID}`,
                template: `
void calc_${nodeID}(float result)
{
    ${assignmentCode}
    ${resultCode}
}
`,
            });
        }

        if (project.runtime === 'simulator') {
            blocks.push({
                location: 'FUNCTION_DEFINITIONS',
                blockType: BlockType.CODE,
                id: `calc_${nodeID}`,
                template: `
function calc_${nodeID}(result)
{
    ${assignmentCode}
    ${resultCode}
}
`,
            });
        }

        return blocks;
    }


    getDetails(): string {
        return `#### Arithmetic
Performs an arithmetic operation on two operands, and saves the result to a **Variable**.
`;
    }
}
